import React, { useState } from 'react';
import { smartAlertService } from '../../services/index.js';

const SmartAlertsManagement = ({ getAdminOrgId, showSuccessNotification, showErrorNotification }) => {
  const [smartAlerts, setSmartAlerts] = useState(null);
  const [smartAlertsLoading, setSmartAlertsLoading] = useState(false);
  const [dispatchingSmartAlerts, setDispatchingSmartAlerts] = useState(false);

  const loadSmartAlertsPreview = async () => {
    try {
      setSmartAlertsLoading(true);
      const orgId = getAdminOrgId();
      const response = await smartAlertService.previewAlerts(orgId);
      setSmartAlerts(response?.data || null);
      showSuccessNotification('Smart Alerts Ready', 'Risk alerts generated successfully.');
    } catch (error) {
      showErrorNotification('Failed to generate alerts', error.message || 'Error occurred');
    } finally {
      setSmartAlertsLoading(false);
    }
  };

  const dispatchSmartAlerts = async () => {
    try {
      setDispatchingSmartAlerts(true);
      const orgId = getAdminOrgId();
      const response = await smartAlertService.dispatchAlerts(orgId);
      const sentCount = response?.data?.notificationsSent ?? 0;
      setSmartAlerts(response?.data?.alerts || smartAlerts);
      showSuccessNotification('Alerts Dispatched', `${sentCount} smart alerts sent to students.`);
    } catch (error) {
      showErrorNotification('Failed to dispatch alerts', error.message || 'Error occurred');
    } finally {
      setDispatchingSmartAlerts(false);
    }
  };

  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">🚨 Smart Alerts</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className="btn btn-info"
            onClick={loadSmartAlertsPreview}
            disabled={smartAlertsLoading}
          >
            {smartAlertsLoading ? 'Loading...' : 'Preview Alerts'}
          </button>
          <button
            className="btn btn-warning"
            onClick={dispatchSmartAlerts}
            disabled={dispatchingSmartAlerts || !smartAlerts}
          >
            {dispatchingSmartAlerts ? 'Sending...' : 'Dispatch Alerts'}
          </button>
        </div>
      </div>

      {!smartAlerts && (
        <div style={{
          background: '#fff3cd',
          border: '1px solid #ffeaa7',
          color: '#856404',
          padding: '12px',
          borderRadius: '6px',
          marginBottom: '16px'
        }}>
          Click <strong>Preview Alerts</strong> to generate today&apos;s risk list.
        </div>
      )}

      {smartAlerts && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            <div className="stat-card"><h3>{smartAlerts?.summary?.totalStudents || 0}</h3><p>Total Students</p></div>
            <div className="stat-card"><h3>{smartAlerts?.summary?.attendanceRiskCount || 0}</h3><p>Low Attendance</p></div>
            <div className="stat-card"><h3>{smartAlerts?.summary?.feeDueCount || 0}</h3><p>Fee Due</p></div>
            <div className="stat-card"><h3>{smartAlerts?.summary?.lowMarksCount || 0}</h3><p>Low Marks</p></div>
          </div>

          <div className="data-table" style={{ marginBottom: '16px' }}>
            <h4 style={{ marginBottom: '8px' }}>Low Attendance</h4>
            <table>
              <thead><tr><th>Student ID</th><th>Name</th><th>Attendance %</th><th>Present / Total</th></tr></thead>
              <tbody>
                {(smartAlerts.attendanceAlerts || []).slice(0, 20).map((a, idx) => (
                  <tr key={`att-${idx}`}>
                    <td>{a.studentId}</td><td>{a.studentName}</td><td>{a.attendancePercentage}%</td><td>{a.presentCount}/{a.totalClasses}</td>
                  </tr>
                ))}
                {(smartAlerts.attendanceAlerts || []).length === 0 && <tr><td colSpan="4" style={{ textAlign: 'center' }}>No low attendance alerts.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="data-table" style={{ marginBottom: '16px' }}>
            <h4 style={{ marginBottom: '8px' }}>Fee Due Alerts</h4>
            <table>
              <thead><tr><th>Student ID</th><th>Name</th><th>Fee Type</th><th>Pending Fee</th></tr></thead>
              <tbody>
                {(smartAlerts.feeAlerts || []).slice(0, 20).map((a, idx) => (
                  <tr key={`fee-${idx}`}>
                    <td>{a.studentId}</td><td>{a.studentName}</td><td>{a.feeType || 'General'}</td><td>₹{Number(a.pendingFee || 0).toLocaleString()}</td>
                  </tr>
                ))}
                {(smartAlerts.feeAlerts || []).length === 0 && <tr><td colSpan="4" style={{ textAlign: 'center' }}>No fee due alerts.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="data-table">
            <h4 style={{ marginBottom: '8px' }}>Low Marks Alerts</h4>
            <table>
              <thead><tr><th>Student ID</th><th>Name</th><th>Average %</th><th>Tests</th></tr></thead>
              <tbody>
                {(smartAlerts.marksAlerts || []).slice(0, 20).map((a, idx) => (
                  <tr key={`marks-${idx}`}>
                    <td>{a.studentId}</td><td>{a.studentName}</td><td>{a.averageScorePercentage}%</td><td>{a.testsConsidered}</td>
                  </tr>
                ))}
                {(smartAlerts.marksAlerts || []).length === 0 && <tr><td colSpan="4" style={{ textAlign: 'center' }}>No low marks alerts.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default SmartAlertsManagement;
